/**
 * Typed game reducer for the local three-story shift.
 * Pure transitions — persistence is applied by the host after reduce.
 */
import {
  deserializeEditGraph,
  resolveEdit,
  serializeEditGraph,
  type ChallengeConstraintId,
  validateEditGraphAgainstPack,
} from "@desk/core";
import {
  createGraphForPack,
  PACK_REGISTRY,
  SHIFT_PACK_ORDER,
  type PackId,
} from "../content/registry.js";
import { landingConfigForPack } from "../content/landing/index.js";
import { reportFault } from "../shell/lib/fault.js";
import {
  bestAudienceResult,
  computeAudienceResult,
  landingContextFromPresentation,
} from "./audience-objective.js";
import {
  CHALLENGE_DEADLINE_CASH_BONUS,
  constraintsSatisfied,
  deadlineBonusEarned,
} from "./challenge/constraints.js";
import { createPublishSnapshot } from "./publish.js";
import type { GamePresentation } from "./challenge/presentation.js";
import type { LandingConfig } from "./emotion-model.js";
import { cashForResult, followersForResult, REWARD_TUNING } from "./rewards.js";
import { getStoreItem } from "./store-catalog.js";
import {
  canStartStory,
  mergeUnlocks,
  nextStoryIndex,
  unlocksAfterCompleting,
} from "./unlocks.js";
import {
  DEFAULT_DRAFT_SLOTS,
  DEFAULT_PORTFOLIO_SLOTS,
  HOME_INTRO_VERSION,
  MAX_DRAFT_SLOTS,
  MAX_PORTFOLIO_SLOTS,
  STARTING_UNLOCKS,
  type AudienceResult,
  type ChallengeSession,
  type CurrentEdit,
  type DraftRecord,
  type EarnedReward,
  type EquippedResultCardTheme,
  type GameSettings,
  type GameState,
  type PortfolioEntry,
  type PublishSnapshot,
  type StoreItemId,
  type UnlockId,
} from "./types.js";

const DEFAULT_SEED = 42;

export type GameStateOverrides = Omit<Partial<GameState>, "settings"> & {
  settings?: Partial<GameSettings>;
};

export function createInitialGameState(
  overrides: GameStateOverrides = {},
): GameState {
  const { settings: settingsOverride, ...rest } = overrides;
  return {
    screen: "home",
    storyIndex: 0,
    completedStoryIds: [],
    economy: { reach: 0, followers: 0, cash: 0, cred: 0 },
    unlocks: [...STARTING_UNLOCKS],
    purchases: [],
    equippedTheme: "default",
    drafts: [],
    portfolio: [],
    draftSlotLimit: DEFAULT_DRAFT_SLOTS,
    portfolioSlotLimit: DEFAULT_PORTFOLIO_SLOTS,
    settings: {
      testSpeed: false,
      homeIntroVersion: 0,
      ...settingsOverride,
    },
    currentEdit: null,
    lastPublish: null,
    publishRevealed: false,
    shiftComplete: false,
    seq: 0,
    audienceProgress: {},
    qaBypass: false,
    challenge: null,
    ...rest,
  };
}

export type GameAction =
  | { type: "SET_TEST_SPEED"; enabled: boolean }
  | { type: "QA_OPEN_EDITOR"; packId: PackId; seed?: number }
  | { type: "START_NEW_SHIFT" }
  | { type: "CONTINUE_SHIFT" }
  | { type: "ENTER_FROM_INTRO" }
  | { type: "OPEN_BRIEF" }
  | { type: "SELECT_AUDIENCE"; crowdId: string }
  | { type: "ENTER_EDITOR"; crowdId?: string }
  | { type: "OPEN_STORY"; packId: PackId; crowdId?: string }
  | { type: "OPEN_ROADMAP" }
  | { type: "CLOSE_ROADMAP" }
  | { type: "UPDATE_EDIT"; graphJson: string }
  | { type: "SAVE_DRAFT" }
  | { type: "LOAD_DRAFT"; draftId: string }
  | { type: "PUBLISH"; presentation?: GamePresentation }
  | { type: "PUBLISH_GRAPH"; graphJson: string; presentation?: GamePresentation }
  | { type: "CONTINUE_AFTER_RESULTS" }
  | { type: "PURCHASE"; itemId: StoreItemId }
  | { type: "EQUIP_THEME"; theme: EquippedResultCardTheme }
  | { type: "SEED_TEST_CASH"; amount: number }
  | { type: "OPEN_PORTFOLIO" }
  | { type: "CLOSE_PORTFOLIO" }
  | { type: "OPEN_RECAP" }
  | { type: "GO_HOME" }
  | { type: "RESET_PROGRESS" }
  | { type: "HYDRATE"; state: GameState }
  | {
      type: "OPEN_CHALLENGE";
      packId: PackId;
      packVersion: string;
      seed: number;
      constraintIds: ChallengeConstraintId[];
      challengerGraphJson: string;
      challengerScore: ChallengeSession["challengerScore"];
      challengerPresentation?: GamePresentation;
    }
  | { type: "OPEN_CHALLENGE_INVALID"; reason: string }
  | { type: "ACCEPT_CHALLENGE"; startedAtMs: number }
  | { type: "DISMISS_CHALLENGE" }
  | { type: "SAVE_CHALLENGE_TO_PORTFOLIO" };

export function reduceGame(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case "SET_TEST_SPEED":
      return {
        ...state,
        settings: { ...state.settings, testSpeed: action.enabled },
      };

    case "QA_OPEN_EDITOR": {
      const entry = PACK_REGISTRY[action.packId];
      if (!entry) return state;
      const seed = action.seed ?? DEFAULT_SEED;
      const graph = createGraphForPack(action.packId, seed);
      const storyIndex = Math.max(0, SHIFT_PACK_ORDER.indexOf(action.packId));
      return {
        ...state,
        qaBypass: true,
        screen: "editor",
        storyIndex,
        challenge: null,
        currentEdit: {
          packId: action.packId,
          packVersion: entry.pack.version,
          seed,
          graphJson: JSON.stringify(graph),
        },
        lastPublish: null,
        publishRevealed: false,
      };
    }

    case "OPEN_CHALLENGE": {
      const entry = PACK_REGISTRY[action.packId];
      if (!entry || entry.pack.version !== action.packVersion) {
        return reduceGame(state, {
          type: "OPEN_CHALLENGE_INVALID",
          reason: "Unknown source pack or version",
        });
      }
      return {
        ...state,
        screen: "challenge-landing",
        qaBypass: false,
        challenge: {
          status: "landing",
          packId: action.packId,
          packVersion: action.packVersion,
          seed: action.seed,
          constraintIds: [...action.constraintIds],
          challengerGraphJson: action.challengerGraphJson,
          ...(action.challengerPresentation
            ? { challengerPresentation: action.challengerPresentation }
            : {}),
          challengerScore: action.challengerScore,
          startedAtMs: null,
          playerEdit: null,
          playerPublish: null,
          publishRevealed: false,
          deadlineBonusAwarded: false,
          campaignFreeze: {
            screen:
              state.screen === "challenge-landing" || state.screen === "challenge-invalid"
                ? "home"
                : state.screen,
            currentEdit: state.currentEdit,
            lastPublish: state.lastPublish,
            publishRevealed: state.publishRevealed,
          },
        },
      };
    }

    case "OPEN_CHALLENGE_INVALID":
      return {
        ...state,
        screen: "challenge-invalid",
        challenge: {
          status: "invalid",
          invalidReason: action.reason,
          // Placeholder for a challenge that failed to parse: it has no real
          // pack, so naming one would be a lie. Only the reason is read.
          packId: SHIFT_PACK_ORDER[0]!,
          packVersion: "",
          seed: 0,
          constraintIds: [],
          challengerGraphJson: "{}",
          challengerScore: {
            reach: 0,
            credDelta: 0,
            contextLoss: 0,
            claimDrift: 0,
            provenanceRisk: 0,
          },
          startedAtMs: null,
          playerEdit: null,
          playerPublish: null,
          publishRevealed: false,
          deadlineBonusAwarded: false,
          campaignFreeze: {
            screen: "home",
            currentEdit: state.currentEdit,
            lastPublish: state.lastPublish,
            publishRevealed: state.publishRevealed,
          },
        },
      };

    case "ACCEPT_CHALLENGE": {
      if (!state.challenge || state.challenge.status === "invalid") return state;
      const ch = state.challenge;
      const entry = PACK_REGISTRY[ch.packId];
      if (!entry) return state;
      const graph = createGraphForPack(ch.packId, ch.seed);
      const playerEdit: CurrentEdit = {
        packId: ch.packId,
        packVersion: ch.packVersion,
        seed: ch.seed,
        graphJson: serializeEditGraph(graph),
      };
      return {
        ...state,
        screen: "editor",
        currentEdit: playerEdit,
        lastPublish: null,
        publishRevealed: false,
        challenge: {
          ...ch,
          status: "editing",
          startedAtMs: action.startedAtMs,
          playerEdit,
          playerPublish: null,
          publishRevealed: false,
          deadlineBonusAwarded: false,
        },
      };
    }

    case "DISMISS_CHALLENGE": {
      if (!state.challenge) return { ...state, screen: "home", challenge: null };
      const freeze = state.challenge.campaignFreeze;
      return {
        ...state,
        challenge: null,
        screen: freeze.screen === "editor" || freeze.screen === "compare"
          ? "home"
          : freeze.screen || "home",
        currentEdit: freeze.currentEdit,
        lastPublish: freeze.lastPublish,
        publishRevealed: freeze.publishRevealed,
      };
    }

    case "SAVE_CHALLENGE_TO_PORTFOLIO": {
      if (!state.challenge?.playerPublish) return state;
      const snap = state.challenge.playerPublish;
      const seq = state.seq + 1;
      const portfolioEntry: PortfolioEntry = {
        id: `pf-challenge-${snap.packId}-${seq}`,
        packId: snap.packId,
        packVersion: snap.packVersion,
        seed: snap.seed,
        graphJson: snap.graphJson,
        score: {
          reach: snap.score.reach,
          credDelta: snap.score.credDelta,
          contextLoss: snap.score.contextLoss,
          claimDrift: snap.score.claimDrift,
          provenanceRisk: snap.score.provenanceRisk,
        },
        rewards: snap.rewards,
        seq,
      };
      return {
        ...state,
        seq,
        portfolio: boundPortfolio(
          [...state.portfolio, portfolioEntry],
          state.portfolioSlotLimit,
        ),
      };
    }

    case "START_NEW_SHIFT":
      return {
        ...createInitialGameState({
          settings: {
            ...state.settings,
            homeIntroVersion: HOME_INTRO_VERSION,
          },
          seq: state.seq + 1,
        }),
        screen: "brief",
        storyIndex: 0,
        currentEdit: makeFreshEdit(0, state.seq + 1),
      };

    case "ENTER_FROM_INTRO": {
      const acknowledged: GameState = {
        ...state,
        settings: {
          ...state.settings,
          homeIntroVersion: HOME_INTRO_VERSION,
        },
      };
      // Defensive: shiftComplete skips intro UI, but still acknowledge + continue.
      if (acknowledged.shiftComplete) {
        return reduceGame(acknowledged, { type: "CONTINUE_SHIFT" });
      }
      if (acknowledged.currentEdit && isCurrentEditValid(acknowledged.currentEdit)) {
        return reduceGame(acknowledged, { type: "CONTINUE_SHIFT" });
      }
      return reduceGame(acknowledged, { type: "START_NEW_SHIFT" });
    }

    case "CONTINUE_SHIFT": {
      if (state.challenge) return state;
      if (state.shiftComplete) {
        return { ...state, screen: "recap" };
      }
      // Single merged results screen: any pending publish resumes on Compare.
      if (state.lastPublish) {
        return { ...state, screen: "compare" };
      }
      if (state.currentEdit && isCurrentEditValid(state.currentEdit)) {
        return { ...state, screen: "editor" };
      }
      const next = nextStoryIndex(state.completedStoryIds);
      if (next === null) {
        return { ...state, shiftComplete: true, screen: "recap", currentEdit: null };
      }
      const gate = canStartStory(next, state.economy.followers, state.completedStoryIds);
      if (!gate.ok) {
        return { ...state, screen: "home", currentEdit: null };
      }
      return {
        ...state,
        storyIndex: next,
        screen: "brief",
        currentEdit: makeFreshEdit(next, state.seq + 1),
        lastPublish: null,
        publishRevealed: false,
        seq: state.seq + 1,
      };
    }

    case "OPEN_BRIEF": {
      if (state.challenge) return state;
      const gate = canStartStory(
        state.storyIndex,
        state.economy.followers,
        state.completedStoryIds,
      );
      if (!gate.ok && !state.qaBypass) return state;
      const edit =
        state.currentEdit && state.currentEdit.packId === SHIFT_PACK_ORDER[state.storyIndex]
          ? state.currentEdit
          : makeFreshEdit(state.storyIndex, state.seq + 1);
      return {
        ...state,
        screen: "brief",
        currentEdit: edit,
        seq: state.currentEdit ? state.seq : state.seq + 1,
        lastPublish: null,
        publishRevealed: false,
      };
    }

    case "SELECT_AUDIENCE": {
      if (!state.currentEdit) return state;
      if (state.currentEdit.chosenCrowdId === action.crowdId) return state;
      return {
        ...state,
        currentEdit: { ...state.currentEdit, chosenCrowdId: action.crowdId },
      };
    }

    case "ENTER_EDITOR": {
      if (state.challenge?.status === "editing" && state.challenge.playerEdit) {
        return {
          ...state,
          screen: "editor",
          currentEdit: state.challenge.playerEdit,
        };
      }
      if (!state.currentEdit || !isCurrentEditValid(state.currentEdit)) {
        const edit = makeFreshEdit(state.storyIndex, state.seq + 1);
        return {
          ...state,
          screen: "editor",
          currentEdit: withChosenCrowd(edit, action.crowdId),
          seq: state.seq + 1,
        };
      }
      return {
        ...state,
        screen: "editor",
        currentEdit: withChosenCrowd(state.currentEdit, action.crowdId),
      };
    }

    case "OPEN_STORY": {
      if (state.challenge) return state;
      const idx = SHIFT_PACK_ORDER.indexOf(action.packId);
      if (idx < 0) return state;
      const entry = PACK_REGISTRY[action.packId];
      const served = landingConfigForPack(action.packId).servedCrowdId;
      const chosenCrowdId = action.crowdId ?? served;
      const seq = state.seq + 1;
      // Prefer the chosen crowd's SAVED cut (earn once, refine freely — Task 4):
      // revisiting a landed audience loads the graph that recorded the best/last
      // result so the prior cut is editable. Else a saved draft for this pack,
      // else a fresh edit. Either way the roadmap origin + chosen crowd are set.
      const savedGraphJson =
        state.audienceProgress[action.packId]?.[chosenCrowdId]?.graphJson;
      const savedEdit = savedGraphJson
        ? editFromSavedGraph(action.packId, entry.pack.version, savedGraphJson)
        : null;
      const draft = state.drafts
        .filter((d) => d.packId === action.packId)
        .sort((a, b) => b.seq - a.seq)[0];
      const baseEdit: CurrentEdit =
        savedEdit ??
        (draft && isDraftValid(draft)
          ? {
              packId: draft.packId,
              packVersion: draft.packVersion,
              seed: draft.seed,
              graphJson: draft.graphJson,
            }
          : makeFreshEdit(idx, seq));
      return {
        ...state,
        screen: "brief",
        storyIndex: idx,
        currentEdit: { ...baseEdit, origin: "roadmap", chosenCrowdId },
        lastPublish: null,
        publishRevealed: false,
        seq,
      };
    }

    case "OPEN_ROADMAP":
      if (state.challenge) return state;
      return { ...state, screen: "roadmap" };

    case "CLOSE_ROADMAP":
      if (state.challenge) return state;
      return { ...state, screen: "home" };

    case "UPDATE_EDIT": {
      if (!state.currentEdit) return state;
      const currentEdit = { ...state.currentEdit, graphJson: action.graphJson };
      if (state.challenge && state.challenge.status === "editing") {
        return {
          ...state,
          currentEdit,
          challenge: {
            ...state.challenge,
            playerEdit: currentEdit,
          },
        };
      }
      return { ...state, currentEdit };
    }

    case "SAVE_DRAFT": {
      if (state.challenge) return state;
      if (!state.currentEdit || !isCurrentEditValid(state.currentEdit)) return state;
      const seq = state.seq + 1;
      const draft: DraftRecord = {
        id: `draft-${state.currentEdit.packId}-${seq}`,
        packId: state.currentEdit.packId,
        packVersion: state.currentEdit.packVersion,
        seed: state.currentEdit.seed,
        graphJson: state.currentEdit.graphJson,
        seq,
      };
      const withoutSame = state.drafts.filter((d) => d.packId !== draft.packId);
      const drafts = boundDrafts([...withoutSame, draft], state.draftSlotLimit);
      return { ...state, drafts, seq };
    }

    case "LOAD_DRAFT": {
      if (state.challenge) return state;
      const draft = state.drafts.find((d) => d.id === action.draftId);
      if (!draft || !isDraftValid(draft)) return state;
      const storyIndex = SHIFT_PACK_ORDER.indexOf(draft.packId);
      if (storyIndex < 0) return state;
      return {
        ...state,
        storyIndex,
        screen: "editor",
        currentEdit: {
          packId: draft.packId,
          packVersion: draft.packVersion,
          seed: draft.seed,
          graphJson: draft.graphJson,
        },
        lastPublish: null,
        publishRevealed: false,
      };
    }

    case "PUBLISH_GRAPH": {
      if (!state.currentEdit) return state;
      const withGraph: GameState = {
        ...state,
        currentEdit: { ...state.currentEdit, graphJson: action.graphJson },
        challenge:
          state.challenge && state.challenge.status === "editing"
            ? {
                ...state.challenge,
                playerEdit: {
                  ...state.currentEdit,
                  graphJson: action.graphJson,
                },
              }
            : state.challenge,
      };
      return reduceGame(withGraph, {
        type: "PUBLISH",
        presentation: action.presentation,
      });
    }

    case "PUBLISH": {
      if (state.challenge && state.challenge.status === "editing") {
        return publishChallengeAttempt(state, action.presentation);
      }
      if (!state.currentEdit || !isCurrentEditValid(state.currentEdit)) return state;
      const packId = state.currentEdit.packId;
      const entry = PACK_REGISTRY[packId];
      let snapshot: PublishSnapshot;
      let audienceResult: AudienceResult;
      try {
        const graph = deserializeEditGraph(state.currentEdit.graphJson);
        snapshot = createPublishSnapshot(packId, entry.pack, graph, action.presentation);
        // Per-audience result (presentation-only): read the same resolved edit
        // through the emotion model + the frozen ScoreBreakdown. Never re-scores.
        const config = landingConfigForPack(packId);
        const chosenCrowdId = state.currentEdit.chosenCrowdId ?? config.servedCrowdId;
        // Reconstruct the editor's landing inputs (effects / caption placement /
        // trims / jump-cuts) so the published result scores the SAME vector the
        // "How it's landing" panel showed — preview == publish (FIX 1/2).
        const landing = landingContextFromPresentation(entry.pack, action.presentation, {
          seamlessTransitions: state.purchases.includes("seamless-transitions"),
        });
        audienceResult = computeAudienceResult(
          resolveEdit(graph, entry.pack),
          config,
          chosenCrowdId,
          snapshot.score,
          landing,
        );
      } catch (e) {
        // Publish is about to become a no-op. Say so somewhere; the editor
        // pre-checks with `publishBlockedReason`, so reaching here means a gate
        // it does not model.
        reportFault("reducer/PUBLISH", e);
        return state;
      }
      // Advance / mark complete only when the published edit LANDED its crowd
      // (Decision D2). A flop keeps the player on the story to re-cut.
      const landed = audienceResult.landed;
      const rewardConfig = landingConfigForPack(packId);
      const chosenCrowd =
        state.currentEdit.chosenCrowdId ?? rewardConfig.servedCrowdId;
      const prevResult = state.audienceProgress[packId]?.[chosenCrowd];
      const firstPublishForCrowd = prevResult === undefined;
      const firstLanding = landed && !prevResult?.landed;

      // Best-score delta reward (Job B): a landing is worth followers/Bits scaled
      // by MATCH QUALITY (fit + a clean bonus), BANKED per (story, crowd). A
      // publish pays only the improvement over the crowd's bank, so a first
      // landing pays full (delta from 0), a better redo pays the difference, and
      // a same-or-worse redo pays 0 — campaign and roadmap replays alike.
      const newRewardFollowers = followersForResult(audienceResult);
      const newRewardCash = cashForResult(audienceResult);
      const bankedFollowers = prevResult?.rewardFollowers ?? 0;
      const bankedCash = prevResult?.rewardCash ?? 0;
      const deltaFollowers = Math.max(0, newRewardFollowers - bankedFollowers);
      const deltaCash = Math.max(0, newRewardCash - bankedCash);

      // Persist the edit graph that produced this result so revisiting loads the
      // prior cut (Task 4). Metrics keep the BEST, the graph is the LATEST, and
      // the banked reward keeps the MAX ever earned for this crowd.
      const resultWithGraph: AudienceResult = {
        ...audienceResult,
        graphJson: state.currentEdit.graphJson,
        rewardFollowers: Math.max(bankedFollowers, newRewardFollowers),
        rewardCash: Math.max(bankedCash, newRewardCash),
      };
      const audienceProgress = mergeAudienceProgress(
        state.audienceProgress,
        packId,
        resultWithGraph,
      );

      // One-time 3/3 clean-sweep bonus: the first publish that lands EVERY crowd
      // in the level. Landing is monotonic (best-kept), so this false→true edge
      // fires exactly once — banking is implicit (never repeats once swept).
      const sweptBefore = allCrowdsLanded(state.audienceProgress[packId], rewardConfig);
      const sweptAfter = allCrowdsLanded(audienceProgress[packId], rewardConfig);
      const cleanSweep = !sweptBefore && sweptAfter;
      const sweepFollowers = cleanSweep ? REWARD_TUNING.sweep.followers : 0;
      const sweepCash = cleanSweep ? REWARD_TUNING.sweep.cash : 0;

      const awardFollowers = deltaFollowers + sweepFollowers;
      const awardCash = deltaCash + sweepCash;

      // Reach (vanity views) and Cred (delayed reputation) are consequences of the
      // FIRST publish for a crowd; a replay re-cuts the SAME reel, so they are not
      // re-applied (earn once, refine freely). Followers/Bits still flow via the
      // delta model above, so a better redo is still rewarded.
      const economy = {
        reach: state.economy.reach + (firstPublishForCrowd ? snapshot.rewards.reachGain : 0),
        followers: state.economy.followers + awardFollowers,
        cash: state.economy.cash + awardCash,
        cred: state.economy.cred + (firstPublishForCrowd ? snapshot.rewards.credDelta : 0),
      };

      const earned: EarnedReward = {
        followers: awardFollowers,
        cash: awardCash,
        improved: deltaFollowers > 0 || deltaCash > 0,
        landed,
        firstLanding,
        cleanSweep,
      };
      const completedStoryIds = landed
        ? uniquePackIds([...state.completedStoryIds, packId])
        : [...state.completedStoryIds];
      const unlocks = landed
        ? mergeUnlocks(state.unlocks, unlocksAfterCompleting(packId))
        : state.unlocks;
      const seq = state.seq + 1;
      const portfolioEntry: PortfolioEntry = {
        id: `pf-${packId}-${seq}`,
        packId,
        packVersion: state.currentEdit.packVersion,
        seed: snapshot.seed,
        graphJson: snapshot.graphJson,
        score: {
          reach: snapshot.score.reach,
          credDelta: snapshot.score.credDelta,
          contextLoss: snapshot.score.contextLoss,
          claimDrift: snapshot.score.claimDrift,
          provenanceRisk: snapshot.score.provenanceRisk,
        },
        rewards: snapshot.rewards,
        seq,
      };
      const portfolio = boundPortfolio(
        [...state.portfolio, portfolioEntry],
        state.portfolioSlotLimit,
      );
      const drafts = state.drafts.filter((d) => d.packId !== packId);
      const nextIdx = nextStoryIndex(completedStoryIds);
      return {
        ...state,
        // Merged post-publish flow (Job 6): publish lands directly on the single
        // results screen — no separate publish→reveal→compare two-step.
        screen: "compare",
        economy,
        unlocks,
        completedStoryIds,
        audienceProgress,
        portfolio,
        drafts,
        lastPublish: { ...snapshot, earned },
        publishRevealed: true,
        seq,
        shiftComplete: nextIdx === null,
      };
    }

    case "CONTINUE_AFTER_RESULTS":
      // Forward action from the single merged results screen (Job 6/7). A
      // challenge run closes back to the campaign freeze; a landed campaign
      // publish advances to the next story / roadmap / recap. (A flop uses
      // Retry — see the screen — and must NOT reach here.)
      if (state.challenge) {
        const freeze = state.challenge.campaignFreeze;
        return {
          ...state,
          challenge: null,
          screen: "home",
          currentEdit: freeze.currentEdit,
          lastPublish: freeze.lastPublish,
          publishRevealed: freeze.publishRevealed,
        };
      }
      if (!state.lastPublish) return state;
      return advanceAfterResults(state);

    case "PURCHASE": {
      if (state.challenge) return state;
      if (state.purchases.includes(action.itemId)) return state;
      const item = getStoreItem(action.itemId);
      if (state.economy.cash < item.cost) return state;
      let draftSlotLimit = state.draftSlotLimit;
      let portfolioSlotLimit = state.portfolioSlotLimit;
      if (action.itemId === "extra-draft-slot") {
        draftSlotLimit = Math.min(MAX_DRAFT_SLOTS, draftSlotLimit + 1);
      }
      void portfolioSlotLimit;
      portfolioSlotLimit = Math.min(MAX_PORTFOLIO_SLOTS, portfolioSlotLimit);
      // Buying the export theme auto-equips it (legacy behavior) when still on default.
      let equippedTheme = state.equippedTheme;
      if (action.itemId === "export-card-theme" && equippedTheme === "default") {
        equippedTheme = "portfolio-alt";
      }
      return {
        ...state,
        economy: { ...state.economy, cash: state.economy.cash - item.cost },
        purchases: [...state.purchases, action.itemId],
        equippedTheme,
        draftSlotLimit,
        portfolioSlotLimit,
      };
    }

    case "EQUIP_THEME": {
      if (state.challenge) return state;
      if (action.theme === "portfolio-alt" && !state.purchases.includes("export-card-theme")) {
        return state;
      }
      if (state.equippedTheme === action.theme) return state;
      return { ...state, equippedTheme: action.theme };
    }

    case "SEED_TEST_CASH": {
      if (!state.settings.testSpeed) return state;
      const amount = Math.max(0, Math.min(9999, Math.floor(action.amount)));
      if (state.economy.cash >= amount) return state;
      return {
        ...state,
        economy: { ...state.economy, cash: amount },
      };
    }

    case "OPEN_PORTFOLIO":
      return { ...state, screen: "portfolio" };

    case "CLOSE_PORTFOLIO":
      return {
        ...state,
        screen: state.challenge
          ? state.challenge.status === "landing"
            ? "challenge-landing"
            : "home"
          : state.shiftComplete
            ? "recap"
            : "home",
      };

    case "OPEN_RECAP":
      return { ...state, screen: "recap", shiftComplete: true };

    case "GO_HOME":
      if (state.challenge) {
        return reduceGame(state, { type: "DISMISS_CHALLENGE" });
      }
      return { ...state, screen: "home" };

    case "RESET_PROGRESS":
      return createInitialGameState({
        settings: {
          testSpeed: state.settings.testSpeed,
          homeIntroVersion: 0,
        },
        seq: state.seq + 1,
      });

    case "HYDRATE":
      return action.state;

    default:
      return state;
  }
}

function publishChallengeAttempt(
  state: GameState,
  presentation?: GamePresentation,
): GameState {
  const ch = state.challenge;
  if (!ch || ch.status !== "editing") return state;
  const edit = ch.playerEdit ?? state.currentEdit;
  if (!edit || !isCurrentEditValid(edit)) return state;
  const entry = PACK_REGISTRY[edit.packId];
  let snapshot: PublishSnapshot;
  try {
    const graph = deserializeEditGraph(edit.graphJson);
    if (!constraintsSatisfied(entry.pack, graph, ch.constraintIds)) {
      return state;
    }
    snapshot = createPublishSnapshot(edit.packId, entry.pack, graph, presentation);
  } catch (e) {
    reportFault("reducer/publishChallengeAttempt", e);
    return state;
  }
  const finishedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  const bonus = deadlineBonusEarned(ch.constraintIds, ch.startedAtMs, finishedAt);
  if (bonus) {
    snapshot = {
      ...snapshot,
      rewards: {
        ...snapshot.rewards,
        cashGain: snapshot.rewards.cashGain + CHALLENGE_DEADLINE_CASH_BONUS,
      },
    };
  }
  return {
    ...state,
    // Merged flow (Job 6): a challenge publish also lands directly on the single
    // results (Compare) screen, revealed — no separate publish→reveal step.
    screen: "compare",
    currentEdit: edit,
    lastPublish: snapshot,
    publishRevealed: true,
    challenge: {
      ...ch,
      status: "compare",
      playerEdit: edit,
      playerPublish: snapshot,
      publishRevealed: true,
      deadlineBonusAwarded: bonus,
    },
  };
}

function advanceAfterResults(state: GameState): GameState {
  // A roadmap REPLAY returns to the Desk hub and never advances the frontier
  // (Decision D2 / campaign-vs-replay branch).
  if (state.currentEdit?.origin === "roadmap") {
    return {
      ...state,
      screen: "roadmap",
      currentEdit: null,
      lastPublish: null,
      publishRevealed: false,
    };
  }
  if (state.shiftComplete) {
    return {
      ...state,
      screen: "recap",
      currentEdit: null,
      lastPublish: null,
      publishRevealed: false,
    };
  }
  const next = nextStoryIndex(state.completedStoryIds);
  if (next === null) {
    return {
      ...state,
      shiftComplete: true,
      screen: "recap",
      currentEdit: null,
      lastPublish: null,
      publishRevealed: false,
    };
  }
  const seq = state.seq + 1;
  return {
    ...state,
    storyIndex: next,
    screen: "brief",
    currentEdit: makeFreshEdit(next, seq),
    lastPublish: null,
    publishRevealed: false,
    seq,
  };
}

function makeFreshEdit(storyIndex: number, seq: number): CurrentEdit {
  const packId = SHIFT_PACK_ORDER[storyIndex] ?? SHIFT_PACK_ORDER[0]!;
  const entry = PACK_REGISTRY[packId];
  const seed = DEFAULT_SEED;
  const graph = createGraphForPack(packId, seed);
  void seq;
  return {
    packId,
    packVersion: entry.pack.version,
    seed,
    graphJson: JSON.stringify(graph),
    origin: "campaign",
  };
}

/**
 * Set the edit's chosen-crowd objective: an explicit pick wins, else keep any
 * existing choice, else default to the pack's served crowd (Decision D5 —
 * default-served, one confirming tap). Presentation-layer metadata only.
 */
function withChosenCrowd(edit: CurrentEdit, crowdId?: string): CurrentEdit {
  const chosenCrowdId =
    crowdId ?? edit.chosenCrowdId ?? landingConfigForPack(edit.packId).servedCrowdId;
  if (edit.chosenCrowdId === chosenCrowdId) return edit;
  return { ...edit, chosenCrowdId };
}

/**
 * Merge one per-audience result into the best-per-(story, crowd) progress map.
 * The recorded METRICS (landed/clean/reach/cred) keep the BEST of old vs. new so
 * a worse replay never lowers a star, but the stored `graphJson` is always the
 * LATEST published cut so revisiting the audience loads what the player last did
 * (Task 4 — earn once, refine freely).
 */
function mergeAudienceProgress(
  progress: GameState["audienceProgress"],
  packId: PackId,
  result: AudienceResult,
): GameState["audienceProgress"] {
  const forStory = { ...(progress[packId] ?? {}) };
  const prev = forStory[result.crowdId];
  const best = bestAudienceResult(prev, result);
  // Metrics keep the BEST (best wins), the graph is the LATEST published cut, and
  // the banked reward keeps the MAX ever earned so a worse replay never lowers
  // the bank (Job B). `result` already carries the running max vs. the old bank.
  forStory[result.crowdId] = {
    ...best,
    graphJson: result.graphJson,
    rewardFollowers: Math.max(prev?.rewardFollowers ?? 0, result.rewardFollowers ?? 0),
    rewardCash: Math.max(prev?.rewardCash ?? 0, result.rewardCash ?? 0),
  };
  return { ...progress, [packId]: forStory };
}

/** True when every crowd in the level has been landed (best-kept progress). */
function allCrowdsLanded(
  forStory: Record<string, AudienceResult> | undefined,
  config: LandingConfig,
): boolean {
  if (!forStory) return false;
  return config.crowdOrder.every((id) => forStory[id]?.landed === true);
}

export function isCurrentEditValid(edit: CurrentEdit): boolean {
  return publishBlockedReason(edit) === null;
}

/**
 * WHY the PUBLISH reducer would refuse this edit, or null when it would go
 * through. Same checks as `isCurrentEditValid`, but it says which one failed.
 *
 * PUBLISH answers a rejected edit with a bare `return state`, so without this
 * the Publish button looks live, does nothing, and reports nothing. That is how
 * a serializer rule that contradicted the resolver stayed invisible: any reel
 * whose caption had been touched twice could not be published at all.
 */
export function publishBlockedReason(edit: CurrentEdit): string | null {
  const entry = PACK_REGISTRY[edit.packId];
  if (!entry) return `Unknown story "${edit.packId}".`;
  if (entry.pack.version !== edit.packVersion) {
    return "This cut was made on an older version of the story. Start a fresh cut to publish.";
  }
  let graph;
  try {
    graph = deserializeEditGraph(edit.graphJson);
  } catch (e) {
    return `This cut could not be saved: ${(e as Error).message}`;
  }
  if (graph.seed !== edit.seed) return "This cut does not match the story it was opened from.";
  try {
    validateEditGraphAgainstPack(graph, entry.pack);
  } catch (e) {
    return `This cut is not valid for the story: ${(e as Error).message}`;
  }
  return null;
}

/**
 * Rebuild a `CurrentEdit` from a persisted per-audience `graphJson` (Task 4).
 * Returns null when the graph fails validation against the current pack so
 * revisiting an audience with a stale/tampered save falls back to a fresh edit.
 */
function editFromSavedGraph(
  packId: PackId,
  packVersion: string,
  graphJson: string,
): CurrentEdit | null {
  try {
    const graph = deserializeEditGraph(graphJson);
    const edit: CurrentEdit = { packId, packVersion, seed: graph.seed, graphJson };
    return isCurrentEditValid(edit) ? edit : null;
  } catch {
    return null;
  }
}

function isDraftValid(draft: DraftRecord): boolean {
  return isCurrentEditValid({
    packId: draft.packId,
    packVersion: draft.packVersion,
    seed: draft.seed,
    graphJson: draft.graphJson,
  });
}

function boundDrafts(drafts: DraftRecord[], limit: number): DraftRecord[] {
  if (drafts.length <= limit) return drafts;
  return [...drafts].sort((a, b) => b.seq - a.seq).slice(0, limit);
}

function boundPortfolio(entries: PortfolioEntry[], limit: number): PortfolioEntry[] {
  if (entries.length <= limit) return entries;
  return [...entries].sort((a, b) => b.seq - a.seq).slice(0, limit);
}

function uniquePackIds(ids: PackId[]): PackId[] {
  const out: PackId[] = [];
  for (const id of ids) {
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

/** Exported for tests — story pack at index. */
export function packIdAt(storyIndex: number): PackId {
  return SHIFT_PACK_ORDER[storyIndex] ?? SHIFT_PACK_ORDER[0]!;
}

export function listUnlocks(state: GameState): UnlockId[] {
  return state.unlocks;
}

/** Strip ephemeral challenge overlay before persisting campaign save. */
export function campaignStateForPersist(state: GameState): GameState {
  const base = !state.challenge
    ? { ...state, challenge: null }
    : (() => {
        const freeze = state.challenge.campaignFreeze;
        return {
          ...state,
          challenge: null,
          screen:
            freeze.screen === "challenge-landing" || freeze.screen === "challenge-invalid"
              ? ("home" as const)
              : freeze.screen,
          currentEdit: freeze.currentEdit,
          lastPublish: freeze.lastPublish,
          publishRevealed: freeze.publishRevealed,
        };
      })();
  // Never persist session testSpeed.
  return {
    ...base,
    settings: { ...base.settings, testSpeed: false },
  };
}
